import { Link } from "react-router-dom";
import "../styles/Projects.css";

const PRODUCTS = [
  {
    id: "stocklens",
    name: "StockLens",
    cat: "Financial intelligence",
    status: "Live",
    statusClass: "is-live",
    tagline: "Intelligence built on evidence, not assumption.",
    desc: "StockLens reads company filings, policy updates, enforcement actions, and ownership records. It turns them into clear financial answers and links every answer to the exact source document.",
    stats: [
      { n: "970K", l: "SEC entities" },
      { n: "15M+", l: "Artifacts structured" },
      { n: "0", l: "Inferred fields" },
    ],
    features: [
      "Reads filings, policy updates, enforcement actions, and ownership records",
      "Links every answer to the source document it came from",
      "Covers filings, policy, enforcement, and company ownership",
      "Clean, organized data that other software can use",
      "Dependable financial data for analysts and AI tools",
      "Never invents missing facts. Every answer comes from a real record",
    ],
    primary: { label: "Visit StockLens", href: "https://www.stocklenshq.com" },
    secondary: { label: "How it works", href: "https://www.stocklenshq.com/engineering" },
    proof: {
      file: "ownership_signal.json",
      state: "validated",
      code: `{
  "issuer": "Example Corp",
  "signal": "insider_purchase",
  "confidence": 0.98,
  "source": {
    "type": "SEC Form 4",
    "accession": "0001234-26-001",
    "evidence_lines": [14, 18]
  }
}`,
      footLeft: "source linked",
      footRight: "no inferred fields",
    },
  },
  {
    id: "mhx",
    name: "MHX",
    cat: "Agentic search",
    status: "In research",
    statusClass: "is-research",
    tagline: "Answers you can cite.",
    desc: "MHX helps AI tools search the web. It returns clean answers, cites the original pages, and ranks sources by how useful they are for the task. Answers built for everyone, never gatekept.",
    features: [
      "Clean, machine ready answers for AI agents",
      "Every answer cited back to the original page",
      "Ranks sources by usefulness for the task",
      "Built as infrastructure other products can call",
    ],
  },
  {
    id: "networkx",
    name: "NetworkX",
    cat: "Trusted networks",
    status: "In development",
    statusClass: "is-building",
    tagline: "Opportunity through real connections.",
    desc: "NetworkX helps people share jobs through friends and trusted contacts instead of anonymous boards. Every introduction is tracked and credited, so opportunity travels person to person.",
    features: [
      "Jobs shared through people you trust",
      "Every introduction tracked and credited",
      "Opportunity that travels person to person",
      "Real trust instead of open, anonymous boards",
    ],
    primary: { label: "Visit NetworkX", href: "https://networkxjobs.com/" },
  },
  {
    id: "billy",
    name: "Bill-y",
    cat: "FinOps for AI",
    status: "In development",
    statusClass: "is-building",
    tagline: "Measuring not how much AI your team uses, but how well.",
    desc: "Bill-y splits an engineering team's AI token spend across the phases of building software, so leaders see whether AI is used efficiently, not merely heavily. Cost clarity so smaller teams are not flying blind.",
    features: [
      "Splits token spend across the phases of building software",
      "Measures efficiency, not volume",
      "Instruments where the work actually happens, per step and per tool",
      "Local and private by design. An efficiency coach, not surveillance",
    ],
    primary: { label: "Explore Bill-y", to: "/bill-y" },
  },
  {
    id: "ilab",
    name: "iLab",
    cat: "Research frontier",
    status: "Open",
    statusClass: "is-research",
    tagline: "The research that improves the core of intelligence.",
    desc: "iLab is where we test better ways for AI to reason, show its sources, and stay safe before those ideas become products. We publish what we learn, in the open.",
    stats: [
      { n: "1.00", l: "O-Funnel F1 (vs 0.20 regex)" },
      { n: "34,989", l: "Records benchmarked" },
      { n: "0", l: "Dependencies" },
    ],
    features: [
      "Better, more careful AI reasoning",
      "Answers linked to the evidence behind them",
      "Reproducible or we do not consider it done",
      "Published openly, methods and results",
    ],
    secondary: { label: "Explore iLab", to: "/ilab" },
  },
];

const MECHANISMS = {
  stocklens: [
    { icon: "bi-file-earmark-text", label: "Primary records", sub: "Filings, policy, ownership" },
    { icon: "bi-cpu", label: "Deterministic parse", sub: "Extract, no inference" },
    { icon: "bi-graph-up-arrow", label: "Source linked signal", sub: "Every field cited" },
    { icon: "bi-patch-check-fill", label: "Verifiable answer", sub: "Trace to the document" },
  ],
  mhx: [
    { icon: "bi-chat-square-text", label: "Question", sub: "In natural language" },
    { icon: "bi-globe2", label: "Read the web", sub: "Fetch and parse pages" },
    { icon: "bi-sort-down", label: "Rank by usefulness", sub: "For the task" },
    { icon: "bi-quote", label: "Cited answer", sub: "Linked to sources" },
  ],
  networkx: [
    { icon: "bi-briefcase", label: "Opportunity", sub: "A role or intro" },
    { icon: "bi-person-check", label: "Trusted contact", sub: "Someone you know" },
    { icon: "bi-arrow-left-right", label: "Tracked introduction", sub: "Recorded and credited" },
    { icon: "bi-people-fill", label: "Real match", sub: "Person to person" },
  ],
  billy: [
    { icon: "bi-coin", label: "AI token spend", sub: "Across the build" },
    { icon: "bi-diagram-3", label: "Split by phase", sub: "Understand, build, test" },
    { icon: "bi-speedometer2", label: "Efficiency signal", sub: "Distribution, not volume" },
    { icon: "bi-shield-check", label: "Waste caught early", sub: "Before the invoice" },
  ],
  ilab: [
    { icon: "bi-lightbulb", label: "Open problem", sub: "Unsolved, load bearing" },
    { icon: "bi-flask", label: "Research and test", sub: "Methods and data" },
    { icon: "bi-arrow-repeat", label: "Reproduce", sub: "Or it is not done" },
    { icon: "bi-journal-text", label: "Publish", sub: "In the open" },
  ],
};

function Mechanism({ steps }) {
  if (!steps) return null;
  return (
    <div className="pr-mech" aria-label="How it works">
      <span className="pr-mech-title">How it works</span>
      <div className="pr-mech-flow">
        <div className="pr-mech-track" aria-hidden="true">
          <span className="pr-mech-pulse" />
        </div>
        {steps.map((s, i) => {
          const out = i === steps.length - 1;
          return (
            <div className={`pr-mech-stage ${out ? "is-out" : ""}`} key={s.label}>
              <div className="pr-mech-node">
                <i className={`bi ${s.icon}`} aria-hidden="true" />
              </div>
              <div className="pr-mech-card">
                <span className="pr-mech-idx">{String(i + 1).padStart(2, "0")}</span>
                <span className="pr-mech-label">{s.label}</span>
                <span className="pr-mech-sub">{s.sub}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductSection({ p, index }) {
  const Btn = ({ b, variant }) => {
    if (!b) return null;
    if (b.href)
      return (
        <a className={`ix-btn ${variant}`} href={b.href} target="_blank" rel="noopener noreferrer">
          {b.label} →
        </a>
      );
    return (
      <Link className={`ix-btn ${variant}`} to={b.to}>
        {b.label} →
      </Link>
    );
  };
  return (
    <section className="ix-section pr-sec" id={p.id}>
      <div className="ix-shell pr-sec-grid">
        <div className="pr-sec-head">
          <span className="pr-sec-idx">{String(index + 1).padStart(2, "0")}</span>
          <span className="front-cat">{p.cat}</span>
          <h2 className="pr-sec-name">
            {p.name}
            <span className={`front-tag ${p.statusClass}`}>{p.status}</span>
          </h2>
          <p className="pr-sec-tag">{p.tagline}</p>
          <p className="pr-sec-desc">{p.desc}</p>
          {p.stats && (
            <div className="pr-stats">
              {p.stats.map((s) => (
                <div className="pr-stat" key={s.l}>
                  <span className="pr-stat-n">{s.n}</span>
                  <span className="pr-stat-l">{s.l}</span>
                </div>
              ))}
            </div>
          )}
          <div className="pr-sec-btns">
            <Btn b={p.primary} variant="ix-btn--primary" />
            <Btn b={p.secondary} variant="ix-btn--ghost" />
          </div>
        </div>
        <div className="pr-sec-features">
          {p.features.map((f) => (
            <div className="pr-feature" key={f}>
              <span className="pr-feature-dot" aria-hidden="true" />
              {f}
            </div>
          ))}
          {p.proof && (
            <div className="pr-proof" aria-label="Example source-traced record">
              <div className="pr-proof-head">
                <span>{p.proof.file}</span>
                <span className="pr-proof-state">{p.proof.state}</span>
              </div>
              <pre>
                <code>{p.proof.code}</code>
              </pre>
              <div className="pr-proof-foot">
                <span>{p.proof.footLeft}</span>
                <span>{p.proof.footRight}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="ix-shell">
        <Mechanism steps={MECHANISMS[p.id]} />
      </div>
    </section>
  );
}

function Projects() {
  return (
    <main className="ix-page">
      <header className="ix-hero center">
        <div className="ix-shell">
          <span className="ix-eyebrow">Our products</span>
          <h1>
            Built by <span className="red">Intellusion.</span>
          </h1>
          <p className="ix-hero-lead">
            Five products, one rule. Show people exactly where the answer came from.
          </p>
          <nav className="pr-jump">
            {PRODUCTS.map((p) => (
              <a key={p.id} href={`#${p.id}`} className="pr-jump-link">
                {p.name}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {PRODUCTS.map((p, i) => (
        <ProductSection p={p} index={i} key={p.id} />
      ))}
    </main>
  );
}

export default Projects;
